import { createRouter, createWebHistory } from "vue-router";
import ActionRoles from "@/helpers/actionRoles";
import { TEAM_ID_KEY, USER_ROLE_KEY } from "@/helpers/localStorageLabels";
import { rolesObject } from "@/helpers/roles";
import { sessionReady } from "@/services/session";
import store from "@/store";
import { AUTH_KEYCLOAK_INIT } from "@/store/actions/auth";

import batchRoutes from "./batchRoutes";

const isAdmin = (_to, _from, next) => {
	const userRole = localStorage.getItem(USER_ROLE_KEY);
	if (ActionRoles.adminRoles.includes(userRole)) {
		next();
		return;
	}
	next("/");
};

const isAdminOrBSA = (_to, _from, next) => {
	const userRole = localStorage.getItem(USER_ROLE_KEY);
	if (
		ActionRoles.adminRoles.includes(userRole) ||
		ActionRoles.bsaRoles.includes(userRole)
	) {
		next();
		return;
	}
	next("/");
};

const isSysAdmin = (_to, _from, next) => {
	const userRole = localStorage.getItem(USER_ROLE_KEY);
	if (ActionRoles.sysAdmin.includes(userRole)) {
		next();
		return;
	}
	next("/");
};

const routes = [
	{
		path: "/login",
		name: "login",
		component: () => import("@/views/LoginView.vue"),
		meta: { guest: true },
	},
	{
		path: "/callback",
		name: "callback",
		component: () => import("@/views/CallbackView.vue"),
		meta: { guest: true },
	},
	{
		path: "/",
		name: "home",
		component: () => import("@/views/StartUp.vue"),
	},
	{
		path: "/supervision/team-select",
		name: "team-select",
		component: () => import("@/views/TeamSelectView.vue"),
	},
	{
		path: "/manual-search",
		name: "querySearch",
		component: () => import("@/views/mnl/MnlSearchView.vue"),
	},
	{
		path: "/tf-search",
		name: "tfSearch",
		component: () => import("@/views/tf/TfSearchView.vue"),
	},
	{
		path: "/query-log",
		name: "queryLog",
		component: () => import("@/views/QueryLog.vue"),
	},
	{
		path: "/ach/query-log",
		name: "achQueryLog",
		component: () => import("@/views/QueryLog.vue"),
	},
	{
		path: "/pkyc/query-log",
		name: "pkycQueryLog",
		component: () => import("@/views/QueryLog.vue"),
	},
	{
		path: "/mnl/query-log",
		name: "mnlQueryLog",
		component: () => import("@/views/QueryLog.vue"),
	},
	{
		path: "/tf/auto-log",
		name: "tfAutoLog",
		component: () => import("@/views/QueryLog.vue"),
	},
	{
		path: "/tf/360-log",
		name: "tf360Log",
		component: () => import("@/views/QueryLog.vue"),
	},
	{
		path: "/tf/799-log",
		name: "tf799Log",
		component: () => import("@/views/QueryLog.vue"),
	},
	{
		path: "/tf/manual-log",
		name: "tfManualLog",
		component: () => import("@/views/QueryLog.vue"),
	},
	{
		path: "/compare-names",
		name: "compareNames",
		component: () => import("@/views/NamesComparisonView"),
	},
	{
		path: "/ach-upload",
		name: "achUpload",
		component: () => import("@/views/UploadView.vue"),
	},
	{
		path: "/pkyc-upload",
		name: "pkycUpload",
		component: () => import("@/views/UploadPkycView.vue"),
	},
	{
		path: "/tf360-upload",
		name: "tf360Upload",
		component: () => import("@/views/UploadTfView.vue"),
	},
	{
		path: "/tf799-upload",
		name: "tf799Upload",
		component: () => import("@/views/UploadTfView.vue"),
	},
	{
		path: "/supervision/wired/queries",
		name: "querylogs",
		component: () => import("@/views/WiredQuerylogsView.vue"),
	},
	{
		path: "/querylog",
		name: "querylogData",
		component: () => import("@/views/QuerylogDataView.vue"),
		props: true,
	},
	{
		path: "/pkyc/querylog",
		name: "pkycQuerylogData",
		component: () => import("@/views/QuerylogDataView.vue"),
		props: true,
	},
	{
		path: "/supervision/wire/tables",
		name: "wireQuerylogs",
		component: () => import("@/views/WiredQuerylogsView.vue"),
	},
	{
		path: "/supervision/wire/dashboard",
		name: "wireDashboard",
		component: () => import("@/views/Dashboards/WireDashboardView.vue"),
	},
	{
		path: "/supervision/tf360/tables",
		name: "tf360Querylogs",
		component: () => import("@/views/TfaQuerylogsView.vue"),
	},
	{
		path: "/supervision/tf360/dashboard",
		name: "tf360Dashboard",
		component: () => import("@/views/Dashboards/TfaDashboardView.vue"),
	},
	{
		path: "/supervision/tf799/tables",
		name: "tf799Querylogs",
		component: () => import("@/views/TfaQuerylogsView.vue"),
	},
	{
		path: "/supervision/tf799/dashboard",
		name: "tf799Dashboard",
		component: () => import("@/views/Dashboards/TfaDashboardView.vue"),
	},
	{
		path: "/:type/dashboard/:finalStatus",
		name: "doughnutTableView",
		component: () => import("@/views/DoughnutTableView.vue"),
		props: true,
	},
	{
		path: "/supervision/ach/tables",
		name: "achQuerylogs",
		component: () => import("@/views/AchQuerylogsView.vue"),
	},
	{
		path: "/supervision/ach/dashboard",
		name: "achDashboard",
		component: () => import("@/views/Dashboards/AchDashboardView.vue"),
	},
	{
		path: "/supervision/ach/audit-trail",
		name: "achAuditTrail",
		component: () => import("@/views/AchAuditTrailView.vue"),
	},
	{
		path: "/supervision/pkyc/tables",
		name: "pkycQuerylogs",
		component: () => import("@/views/PkycQuerylogsView.vue"),
	},
	{
		path: "/supervision/pkyc/dashboard",
		name: "pkycDashboard",
		component: () => import("@/views/Dashboards/PkycDashboardView.vue"),
	},
	{
		path: "/supervision/mnl/tables",
		name: "mnlQueryLogs",
		component: () => import("@/views/MnlQuerylogsView.vue"),
	},
	{
		path: "/reports/daily",
		name: "dailyReportsView",
		component: () => import("@/views/reports/DailyReportsView.vue"),
	},
	{
		path: "/reports",
		name: "sysAdminReportsView",
		component: () => import("@/views/reports/SysAdminReportsView.vue"),
	},
	{
		path: "/reports/extended-statistics",
		name: "extendedStatistics",
		component: () => import("@/views/reports/ExtendedStatisticsReport.vue"),
		beforeEnter: isSysAdmin,
	},
	{
		path: "/profile",
		name: "profile",
		component: () => import("@/views/UserProfile"),
	},
	{
		path: "/admin/user-management",
		name: "userManagement",
		component: () => import("@/views/UserManagement"),
	},
	{
		path: "/admin/user-audit",
		name: "userAudit",
		component: () => import("@/views/UserAudit"),
	},
	{
		path: "/admin/system-configuration",
		name: "systemConfiguration",
		component: () => import("@/views/SystemConfiguration"),
	},
	{
		path: "/supervision/users",
		name: "chiefUserDashboard",
		component: () => import("@/views/ChiefUserDashboard"),
	},
	{
		path: "/admin/persistence",
		name: "persistence",
		component: () => import("@/views/PersistenceView"),
	},
	{
		path: "/admin/summary-reports",
		name: "summaryReports",
		component: () => import("@/views/admin/SummaryReports"),
	},
	{
		path: "/admin/available-sources",
		name: "availableSources",
		component: () => import("@/views/admin/AvailableSources"),
	},
	{
		path: "/admin/sdn-updates",
		name: "sdnUpdates",
		component: () => import("@/views/admin/SdnUpdates"),
	},
	{
		path: "/admin/custom-sources",
		name: "customSources",
		component: () => import("@/views/admin/CustomSources"),
	},
	{
		path: "/admin/custom-countries",
		name: "customCountries",
		component: () => import("@/views/admin/CustomCountries"),
	},
	{
		path: "/admin/custom-accounts",
		name: "customAccounts",
		component: () => import("@/views/admin/CustomAccounts"),
	},
	{
		path: "/admin/data-sources",
		name: "dataSources",
		component: () => import("@/views/admin/DataSources"),
	},
	{
		path: "/admin/tenant-management",
		name: "tenantManagement",
		component: () => import("@/views/TenantManagement"),
		beforeEnter: isAdmin,
	},
	{
		path: "/admin/tenant-audit",
		name: "tenantAudit",
		component: () => import("@/views/TenantAuditView"),
		beforeEnter: isAdmin,
	},
	{
		path: "/admin/system-control",
		name: "systemControl",
		component: () => import("@/views/SystemControl"),
		beforeEnter: isAdmin,
	},
	{
		path: "/admin/pkyc-persistence",
		name: "pkycPersistence",
		component: () => import("@/views/PersistenceView"),
		beforeEnter: isAdminOrBSA,
	},
	{
		path: "/admin/ach-persistence",
		name: "achPersistence",
		component: () => import("@/views/PersistenceView"),
		beforeEnter: isAdminOrBSA,
	},
	{
		path: "/tutorial",
		name: "tutorial",
		meta: {
			external: true,
			url: "https://fincom.co/wp-admin/?wtlwp_token=8b5bbba73ec199f8d622d92c0c35377ef005ae836520304a8b04d9f58b654de4089440f51f7ca3e39c25028ba5009b1df28ed97becc7d2403999cd20bda40a21",
		},
	},
	...batchRoutes,
];

const router = createRouter({
	history: createWebHistory(process.env.BASE_URL),
	routes,
});

let refreshAttempted = false;

router.beforeEach(async (to, _from, next) => {
	if (to.meta.external) {
		window.open(to.meta.url, "_blank");
		next(false);
		return;
	}

	// Block navigation until bootstrap's session-restore attempt has finished.
	// Without this, the first navigation can read isAuthenticated before the
	// in-memory access token has been refreshed, causing API calls to fire
	// without a Bearer header.
	await sessionReady;

	let isAuthenticated = store.getters.isAuthenticated;
	const isGuestRoute = to.meta.guest;

	console.log("[router guard]", {
		to: to.path,
		isAuthenticated,
		isGuestRoute,
		storeToken: !!store.state.auth?.token,
		cookies: document.cookie.substring(0, 100),
	});

	// If not authenticated, try to restore session via refresh token cookie (once).
	// The cookie is HttpOnly so we can't check document.cookie — just attempt the call.
	if (!isAuthenticated && !isGuestRoute && !refreshAttempted) {
		refreshAttempted = true;
		try {
			console.log(
				"[router guard] Not authenticated, attempting token refresh...",
			);
			const { refreshAccessToken } = await import("@/services/authService");
			await refreshAccessToken();
			console.log(
				"[router guard] Token refreshed, dispatching AUTH_KEYCLOAK_INIT...",
			);
			await store.dispatch(AUTH_KEYCLOAK_INIT);
			isAuthenticated = store.getters.isAuthenticated;
			console.log("[router guard] Session restored:", { isAuthenticated });
			if (isAuthenticated) {
				refreshAttempted = false; // Reset for future refreshes
			}
		} catch (err) {
			console.log("[router guard] Refresh failed:", err.message);
		}
	}

	// Redirect unauthenticated users to login (except guest routes)
	if (!isAuthenticated && !isGuestRoute) {
		console.log("[router guard] Not authenticated, redirecting to login");
		// Save intended destination for post-login redirect
		if (to.fullPath !== "/") {
			sessionStorage.setItem("oauth_redirect", to.fullPath);
		}
		return next({ name: "login" });
	}

	// Redirect authenticated users away from guest-only routes
	if (isAuthenticated && isGuestRoute) {
		return next("/");
	}

	// Force team selection for supervisors who haven't picked a team yet.
	// Selection lives in localStorage only and is wiped on logout, so
	// multi-team supervisors land here on every fresh login.
	const role = store.getters.userRole || localStorage.getItem(USER_ROLE_KEY);
	if (
		role === rolesObject.SUPERVISOR &&
		!localStorage.getItem(TEAM_ID_KEY) &&
		!to.path.includes("/supervision/team-select")
	) {
		return next("/supervision/team-select");
	}

	next();
});

export default router;
